/**
 * Poseidon dashboard — per-trade decision-trail page (§3.2 B).
 *
 * Route: /trades/:completedTradeId
 */
import { Link, useParams } from 'react-router-dom';
import { DecisionTrail } from '../components/DecisionTrail';
import { SignalBreakdownPanel } from '../components/SignalBreakdownPanel';
import { ErrorState, LoadingState, Panel } from '../components/primitives';
import { useApiData } from '../hooks/useApiData';
import { useServices } from '../state/Services';

export function TradeDetail(): JSX.Element {
  const { completedTradeId } = useParams<{ completedTradeId: string }>();
  const { api, isMock } = useServices();
  const id = completedTradeId ?? '';

  const trail = useApiData(`trail-${id}`, () => api.getDecisionTrail(id), { enabled: id.length > 0, mode: api.mode });
  const decisionId = trail.data?.decision_id ?? null;

  const breakdown = useApiData(`breakdown-${decisionId ?? 'pending'}`, () => api.getSignalBreakdown(decisionId ?? ''), {
    enabled: decisionId !== null,
    mode: api.mode,
  });

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Trade {id}</h1>
          <div className="page-header__spec">
            §3.2 B decision trail · signal → feature snapshot → research → risk rules → approval → MT5 request/result → broker deals →
            final net P&amp;L and tags
          </div>
        </div>
        <div className="data-clock">
          <Link to="/trades">← all trades</Link>
          {isMock && <span className="mock-note">mock values</span>}
        </div>
      </header>

      {id.length === 0 && (
        <Panel title="No trade selected">
          <p className="text-muted">
            This route needs a completed trade id. Open one from the <Link to="/trades">trades list</Link>.
          </p>
        </Panel>
      )}

      {id.length > 0 && trail.error !== null && trail.data === null && (
        <ErrorState
          error={trail.error}
          context="The decision trail for this trade could not be loaded. A trade without a trail cannot be reviewed."
          onRetry={trail.refresh}
        />
      )}

      {id.length > 0 && trail.data === null && trail.error === null && <LoadingState label="Loading decision trail…" />}

      {trail.data !== null && (
        <>
          <Panel
            title="Decision trail"
            subtitle="every stored step, in order"
            mock={isMock}
            footnote="Steps that genuinely did not happen are labelled, not omitted. Corrections are appended as new revisions rather than overwriting a result (§8.2)."
          >
            <DecisionTrail trail={trail.data} mock={isMock} />
          </Panel>

          {decisionId !== null && (
            <>
              {breakdown.error !== null && breakdown.data === null && (
                <ErrorState error={breakdown.error} context="The explainable signal breakdown could not be loaded." onRetry={breakdown.refresh} />
              )}
              {breakdown.data === null && breakdown.error === null && <LoadingState label="Loading signal breakdown…" />}
              {breakdown.data !== null && <SignalBreakdownPanel breakdown={breakdown.data} mock={isMock} />}
            </>
          )}
        </>
      )}
    </>
  );
}
